"use client";

import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, Search } from "lucide-react";
import { useId, useMemo, useRef, useState } from "react";
import { cn } from "./cn";
import { Field } from "./Field";

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  label: string;
  helper?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  loading?: boolean;
  options: ComboboxOption[];
  value: string | undefined;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  /** Stable id for the trigger (label `htmlFor` target). Auto-generated when omitted. */
  id?: string;
  /** Accessible name of the search input inside the popover. */
  searchLabel?: string;
  /**
   * Remote mode: when set, typing calls this (debounce on the caller side if
   * needed) and `options` are shown as-is — the server already filtered them.
   * Use for lists that are paginated/searchable in the API (clientes, pessoas).
   */
  onSearchChange?: (query: string) => void;
  /** Remote mode: renders "Carregar mais" at the end of the list. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  /** Label shown in the trigger when the selected value is not in the current `options` page. */
  selectedLabel?: string;
}

/** Searchable single-select — use when there are more than ~10 options. */
export function Combobox({
  label,
  helper,
  error,
  required,
  placeholder = "Buscar…",
  emptyLabel = "Nenhum resultado",
  loading = false,
  options,
  value,
  onValueChange,
  disabled,
  id,
  searchLabel,
  onSearchChange,
  hasMore = false,
  onLoadMore,
  loadingMore = false,
  selectedLabel,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const listId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    if (onSearchChange) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => option.label.toLowerCase().includes(q));
  }, [options, query, onSearchChange]);

  const selectedOption = options.find((option) => option.value === value);
  const triggerLabel =
    selectedOption?.label ?? (value !== undefined && value !== "" ? selectedLabel : undefined);

  function commit(option: ComboboxOption) {
    onValueChange(option.value);
    setOpen(false);
    setQuery("");
    onSearchChange?.("");
    triggerRef.current?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((index) => Math.min(index + 1, filtered.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((index) => Math.max(index - 1, 0));
    } else if (event.key === "Home") {
      event.preventDefault();
      setHighlighted(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setHighlighted(filtered.length - 1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = filtered[highlighted];
      if (option) commit(option);
    } else if (event.key === "Escape") {
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  return (
    <Field label={label} helper={helper} error={error} required={required} htmlFor={id}>
      {({ fieldId, describedBy }) => (
        <Popover.Root
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (next) setHighlighted(0);
            else if (query) {
              setQuery("");
              onSearchChange?.("");
            }
          }}
        >
          <Popover.Trigger asChild>
            <button
              ref={triggerRef}
              id={fieldId}
              type="button"
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-invalid={Boolean(error) || undefined}
              aria-describedby={describedBy}
              disabled={disabled}
              className="ui-select-trigger"
            >
              <span
                className={cn("ui-multiselect-summary", !triggerLabel && "ui-combobox-placeholder")}
              >
                {triggerLabel ?? placeholder}
              </span>
              <ChevronDown size={16} className="ui-select-icon" aria-hidden="true" />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              className="ui-combobox-content"
              sideOffset={4}
              align="start"
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                searchRef.current?.focus();
              }}
            >
              <div className="ui-combobox-search">
                <Search size={14} aria-hidden="true" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setHighlighted(0);
                    onSearchChange?.(event.target.value);
                  }}
                  onKeyDown={onKeyDown}
                  placeholder={searchLabel ?? "Buscar…"}
                  aria-label={searchLabel ?? "Buscar"}
                  aria-controls={listId}
                  aria-activedescendant={
                    filtered[highlighted] ? `${listId}-${filtered[highlighted].value}` : undefined
                  }
                />
              </div>
              <ul id={listId} role="listbox" className="ui-combobox-list">
                {loading ? (
                  <li className="ui-combobox-empty">Carregando…</li>
                ) : filtered.length === 0 ? (
                  <li className="ui-combobox-empty">{emptyLabel}</li>
                ) : (
                  filtered.map((option, index) => (
                    <li
                      key={option.value}
                      id={`${listId}-${option.value}`}
                      role="option"
                      aria-selected={option.value === value}
                      data-highlighted={index === highlighted}
                      data-selected={option.value === value}
                      className="ui-combobox-item"
                      onMouseEnter={() => setHighlighted(index)}
                      onClick={() => commit(option)}
                    >
                      <span className="ui-select-item-text">{option.label}</span>
                      {option.value === value ? (
                        <Check size={15} className="ui-select-item-indicator" aria-hidden="true" />
                      ) : null}
                    </li>
                  ))
                )}
              </ul>
              {hasMore && onLoadMore ? (
                <div className="ui-combobox-more">
                  <button type="button" onClick={onLoadMore} disabled={loadingMore}>
                    {loadingMore ? "Carregando…" : "Carregar mais"}
                  </button>
                </div>
              ) : null}
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}
    </Field>
  );
}
